import { SurveyAnswers } from "../../common/hooks/api/useSurvey";
import { SurveyQuestionProps } from "../components/get-compliant/survey/SurveyQuestion";
import { SurveyDefinition } from "./survey";

export const HR_SURVEY_NAME = "hr-survey";

const hrQuestions = (answers: SurveyAnswers): SurveyQuestionProps[] => {
  // When adding or removing a question slug, also update SurveySlugs.HR_SURVEY_SLUGS on the backend
  return [
    californiaEmployeesQuestion(answers), // "business-employees-ca"
    californiaContractorsQuestion(answers), // "business-contractors-ca"
    californiaJobApplicantsQuestion(answers), // "business-job-applicants-ca"
    coloradoEmployeesQuestion(answers), // "business-employees-co"
    coloradoBiometricIdentifiersQuestion(answers), // "business-biometric-identifiers-co"
    eeaUkEmployeesQuestion(answers), // "employees-eea-uk"
  ];
};

export const hrVisibility = (answers: SurveyAnswers, isGdpr: boolean): Record<string, boolean> => {
  // When updating visibility, also update OrganizationOnboardingProgressService.countUnansweredHrSurveyQuestion()
  return {
    "business-employees-ca": true,
    "business-contractors-ca": true,
    "business-job-applicants-ca": true,
    [COLORADO_EMPLOYEES_QUESTION_SLUG]: true,
    [COLORADO_BIOMETRIC_IDENTIFIERS_QUESTION_SLUG]:
      answers[COLORADO_EMPLOYEES_QUESTION_SLUG] === "true",
    "employees-eea-uk": isGdpr,
  };
};

export const hrSurvey = (answers: SurveyAnswers, isGdpr: boolean): SurveyDefinition => {
  return {
    questions: hrQuestions(answers),
    visibility: hrVisibility(answers, isGdpr),
    disabled: {},
    relatedQuestionGroups: [
      [COLORADO_EMPLOYEES_QUESTION_SLUG, COLORADO_BIOMETRIC_IDENTIFIERS_QUESTION_SLUG],
    ],
  };
};

export const californiaEmployeesQuestion = (answers: SurveyAnswers): SurveyQuestionProps => {
  const slug = "business-employees-ca";

  return {
    type: "boolean",
    slug: slug,
    question: "Does your business have employees in California?",
    noConfirmation:
      answers[slug] === "true"
        ? "Are you sure you would like to change your answer? All data mapping for this employment group will be deleted."
        : undefined,
  };
};

export const californiaContractorsQuestion = (answers: SurveyAnswers): SurveyQuestionProps => {
  const slug = "business-contractors-ca";

  return {
    type: "boolean",
    slug: slug,
    question: "Does your business hire individual contractors in California?",
    helpText:
      'For example, 1099 employees. Answer "no" if you only hire contractors through agencies or contracting businesses.',
    noConfirmation:
      answers[slug] === "true"
        ? "Are you sure you would like to change your answer? All data mapping for this employment group will be deleted."
        : undefined,
  };
};

export const californiaJobApplicantsQuestion = (answers: SurveyAnswers): SurveyQuestionProps => {
  const slug = "business-job-applicants-ca";

  return {
    type: "boolean",
    slug: slug,
    question: "Does your business collect personal information about job applicants in California?",
    noConfirmation:
      answers[slug] === "true"
        ? "Are you sure you would like to change your answer? All data mapping for this employment group will be deleted."
        : undefined,
  };
};

export const eeaUkEmployeesQuestion = (answers: SurveyAnswers): SurveyQuestionProps => {
  const slug = "employees-eea-uk";

  return {
    type: "boolean",
    slug: slug,
    question: "Does your business have workers (employees or contractors) living in the EEA/UK/CH?",
    noConfirmation:
      answers[slug] === "true"
        ? "Are you sure you would like to change your answer? All data mapping for this employment group will be deleted."
        : undefined,
  };
};

export const COLORADO_EMPLOYEES_QUESTION_SLUG = "business-employees-co";
export const coloradoEmployeesQuestion = (_answers: SurveyAnswers): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: COLORADO_EMPLOYEES_QUESTION_SLUG,
    question: "Does your business have employees or contractors in Colorado?",
    helpText:
      "Colorado has special requirements for businesses that collect biometric data, including such data about employees. We'll ask a few questions to see if this applies. If it does, we'll ask you to complete the required steps.",
  };
};

export const COLORADO_BIOMETRIC_IDENTIFIERS_QUESTION_SLUG = "business-biometric-identifiers-co";
export const coloradoBiometricIdentifiersQuestion = (
  _answers: SurveyAnswers,
): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: COLORADO_BIOMETRIC_IDENTIFIERS_QUESTION_SLUG,
    question:
      'Does your business collect "biometric identifiers" about employees or contractors in Colorado?',
    helpText:
      "Biometric identifiers are data related to a person's biological, physical, or behavioral characteristics, if that data can be used to identify that person. For example, fingerprints, voiceprints, facial geometry, or physical patterns or characteristics that can uniquely identify someone. For example, using facial scans or fingerprints to clock employees in and out of work shifts or to gain access to work premises.",
  };
};
